import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Select,
  FormControl,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  ShoppingCart as OrdersIcon,
  LocalShipping as DeliveryIcon,
  Campaign as MarketingIcon,
  PersonAddAlt1 as LeadsIcon,
  Timeline as PipelineIcon,
  Insights as AnalyticsIcon,
  Event as CalendarIcon,
  Groups as GroupsIcon,
  Share as ShareIcon,
  Person as PersonIcon,
  Recommend as RecommendIcon,
  Map as MapIcon,
  LocationOn as LocationIcon,
  AccountCircle as AccountIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  Extension as PluginIcon,
  PhotoLibrary,
  ShoppingCart,
} from '@mui/icons-material';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation as useLocationContext } from '../../contexts/LocationContext';
import api from '../../utils/api';

const drawerWidth = 280;

const DashboardLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  // Sidebar now uses flat sections without dropdowns
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const [pluginMenuItems, setPluginMenuItems] = useState([]);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { locations, selectedLocation, selectLocation, loading: locationLoading } = useLocationContext();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // No dropdown toggles needed for flat sections

  // Load active plugin menu items
  const loadPluginMenuItems = async () => {
    try {
      const response = await api.get('/plugins/active-menu');
      if (response.data.success) {
        setPluginMenuItems(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load plugin menu items:', error);
    }
  };

  // Initial load on user ready
  useEffect(() => {
    if (user) {
      loadPluginMenuItems();
    }
  }, [user]);

  // Refresh plugin menu on route changes so newly activated plugins appear without reload
  useEffect(() => {
    if (user) {
      loadPluginMenuItems();
    }
  }, [location.pathname]);

  // Listen for plugin updates from PluginsPage to refresh sidebar immediately
  useEffect(() => {
    const handler = () => {
      if (user) loadPluginMenuItems();
    };
    window.addEventListener('plugins:updated', handler);
    return () => window.removeEventListener('plugins:updated', handler);
  }, [user]);

  const handleProfileMenuOpen = (event) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    handleProfileMenuClose();
  };

  // Removed auto-redirect to settings - let users navigate freely

  const handleLocationChange = (event) => {
    const locationId = event.target.value;
    // Find location with safer string comparison for MongoDB ObjectIds
    const location = locations && locations.find(loc => 
      loc._id && locationId && loc._id.toString() === locationId.toString()
    );
    
    if (location) {
      console.log('Changing location to:', location);
      selectLocation(location);
    }
  };

  const isActivePath = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const getPluginIcon = (iconName) => {
    switch (iconName) {
      case 'PhotoLibrary':
        return <PhotoLibrary />;
      case 'ShoppingCart':
        return <ShoppingCart />;
      default:
        return <PluginIcon />;
    }
  };

  const sidebarItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
      exact: true,
    },
    {
      text: 'Customers',
      icon: <AccountIcon />,
      path: '/dashboard/customers',
    },
    {
      text: 'Orders',
      icon: <OrdersIcon />,
      path: '/dashboard/orders',
    },
    {
      text: 'Inventory',
      icon: <InventoryIcon />,
      path: '/dashboard/inventory',
    },
    {
      text: 'Deliveries',
      icon: <DeliveryIcon />,
      path: '/dashboard/deliveries',
    },
    {
      text: 'Reports',
      icon: <AnalyticsIcon />,
      path: '/dashboard/reports',
    },
    {
      text: 'User Management',
      icon: <GroupsIcon />,
      path: '/dashboard/users',
    },
    {
      text: 'Plugins',
      icon: <PluginIcon />,
      path: '/dashboard/plugins',
    },
  ];

  const marketingItems = [
    // Marketing menu items removed per user request
  ];

  const drawer = (
    <Box>
      {/* Logo */}
      <Box sx={{ p: 3, textAlign: 'center', borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          DymenStock
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Inventory Management
        </Typography>
      </Box>

      {/* Navigation */}
      <List sx={{ px: 2, py: 1 }}>
        {/* Section: Main */}
        <Typography variant="overline" sx={{ px: 2, color: 'text.secondary' }}>Main</Typography>
        {sidebarItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              disabled={!selectedLocation}
              onClick={() => navigate(item.path)}
              selected={item.exact ? location.pathname === item.path : isActivePath(item.path)}
              sx={{
                borderRadius: 2,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}

        {/* Section: Plugins */}
        {pluginMenuItems.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="overline" sx={{ px: 2, color: 'text.secondary' }}>Plugins</Typography>
            {pluginMenuItems.map((item) => (
              <ListItem key={item.label} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  disabled={!selectedLocation}
                  onClick={() => navigate(item.path)}
                  selected={isActivePath(item.path)}
                  sx={{
                    borderRadius: 2,
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                      '& .MuiListItemIcon-root': {
                        color: 'white',
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {getPluginIcon(item.icon)}
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </>
        )}

        {/* Section: Settings */}
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="overline" sx={{ px: 2, color: 'text.secondary' }}>Settings</Typography>
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            onClick={() => navigate('/dashboard/settings')}
            selected={isActivePath('/dashboard/settings')}
            sx={{
              borderRadius: 2,
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                '& .MuiListItemIcon-root': {
                  color: 'white',
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Settings" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { lg: `calc(100% - ${drawerWidth}px)` },
          ml: { lg: `${drawerWidth}px` },
          backgroundColor: 'background.paper',
          color: 'text.primary',
          boxShadow: 1,
          borderRadius: 0,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { lg: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Location Selector */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={selectedLocation?._id || ''}
                onChange={handleLocationChange}
                displayEmpty
                sx={{ fontSize: '0.9rem' }}
              >
                {locations && locations.map((location) => (
                  <MenuItem key={location._id} value={location._id}>
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedLocation && (
              <Chip 
                label="Active" 
                size="small" 
                color="success" 
                sx={{ ml: 1 }} 
              />
            )}
          </Box>

          {/* User Profile */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>
              Welcome, {user?.name || user?.email}
            </Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32, backgroundColor: 'primary.main' }}>
                {(user?.name || user?.email)?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
          </Box>

          {/* Profile Menu */}
          <Menu
            anchorEl={profileMenuAnchor}
            open={Boolean(profileMenuAnchor)}
            onClose={handleProfileMenuClose}
            onClick={handleProfileMenuClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={() => { navigate('/dashboard/profile'); handleProfileMenuClose(); }}>
              <AccountIcon sx={{ mr: 2 }} />
              Profile
            </MenuItem>
            <MenuItem onClick={() => { navigate('/dashboard/settings'); handleProfileMenuClose(); }}>
              <SettingsIcon sx={{ mr: 2 }} />
              Settings
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ mr: 2 }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { lg: drawerWidth }, flexShrink: { lg: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', lg: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', lg: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { lg: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        <Toolbar />
        <Box sx={{ p: location.pathname.includes('/plugins/photo-adds') || location.pathname.includes('/plugins/storefront-3d') ? 0 : 3 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardLayout;
